<template>
  <div class="course-layout-v4">
    <Loading v-model:active="loadingStore.isLoading" :can-cancel="false" :is-full-page="true" />

    <header class="topbar">
      <div class="topbar__left">
        <button class="icon-button mobile-only" @click="toggleSidebar">
          <i class="fa fa-bars"></i>
        </button>
        <router-link to="/caduceus/dashboard" class="brand">
          <img src="@/assets/img/caduceus.png" alt="Caduceus" class="brand__logo">
          <div class="brand__copy">
            <span class="brand__label">{{ isPreview ? 'Preview' : 'Área do aluno' }}</span>
            <strong>{{ course?.title || 'Curso' }}</strong>
          </div>
        </router-link>
      </div>

      <div class="topbar__right">
        <div class="student-chip">
          <span class="student-chip__label">Aluno</span>
          <strong>{{ studentName || 'Visitante' }}</strong>
        </div>
      </div>
    </header>

    <div class="shell">
      <aside class="sidebar" :class="{ 'sidebar--open': sidebarOpen }">
        <div class="sidebar__panel">
          <div class="sidebar__head">
            <div>
              <p class="eyebrow">Conteúdo</p>
              <h2>Trilha do curso</h2>
            </div>
            <button class="icon-button mobile-only" @click="toggleSidebar">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <div class="sidebar__summary">
            <div class="summary-card">
              <span>Aulas liberadas</span>
              <strong>{{ enabledLessons.length }}</strong>
            </div>
            <div class="summary-card">
              <span>Progresso visual</span>
              <strong>{{ currentLessonIndexLabel }}</strong>
            </div>
          </div>

          <div class="module-list">
            <section v-for="(module, moduleIndex) in course?.modules" :key="module.id" class="module-card">
              <button class="module-card__header" @click="toggleModule(moduleIndex)">
                <div>
                  <p class="module-card__label">Módulo {{ moduleIndex + 1 }}</p>
                  <h3>{{ module.title }}</h3>
                </div>
                <div class="module-card__meta">
                  <span>{{ getEnabledLessonsCount(module) }}</span>
                  <i class="fas fa-chevron-down" :class="{ 'is-open': expandedModules[moduleIndex] }"></i>
                </div>
              </button>

              <transition name="fade-slide">
                <div v-show="expandedModules[moduleIndex]" class="lesson-list">
                  <button
                    v-for="lesson in module.lessons"
                    v-show="lesson.isEnabled"
                    :key="lesson.id"
                    class="lesson-item"
                    :class="{ 'lesson-item--active': currentLesson.id === lesson.id }"
                    @click="selectLesson(module, lesson)"
                  >
                    <span class="lesson-item__icon">
                      <i class="fas fa-play-circle" v-if="lesson.contentType === 'YOUTUBE' || lesson.contentType === 'PANDA'"></i>
                      <i class="fas fa-file-pdf" v-else-if="lesson.contentType === 'PDF'"></i>
                      <i class="fas fa-question-circle" v-else-if="lesson.contentType === 'QUIZ'"></i>
                      <i class="fas fa-file-alt" v-else></i>
                    </span>
                    <span class="lesson-item__body">
                      <strong>{{ lesson.title }}</strong>
                      <small>{{ getLessonType(lesson.contentType) }}</small>
                    </span>
                  </button>
                </div>
              </transition>
            </section>

            <button
              v-if="isCertificateAvailable && !isPreview"
              class="certificate-entry"
              @click="selectCertificate"
            >
              <span class="certificate-entry__icon"><i class="fas fa-award"></i></span>
              <span class="certificate-entry__body">
                <strong>Certificado</strong>
                <small>Baixe ao concluir o curso</small>
              </span>
            </button>
          </div>
        </div>
      </aside>

      <main class="content">
        <section class="hero-card">
          <div>
            <p class="eyebrow">{{ currentModule?.title || 'Aula atual' }}</p>
            <h1>{{ contentTitle }}</h1>
            <p class="hero-card__subtitle">{{ contentSubtitle }}</p>
          </div>

          <div class="hero-card__actions" v-if="selectedContent === 'lesson'">
            <button class="nav-button" :disabled="!previousLesson" @click="goToLesson(previousLesson)">
              <i class="fa fa-arrow-left"></i>
              <span>Anterior</span>
            </button>
            <button class="nav-button nav-button--primary" :disabled="!nextLesson" @click="goToLesson(nextLesson)">
              <span>Próxima</span>
              <i class="fa fa-arrow-right"></i>
            </button>
          </div>
        </section>

        <section class="player-card" v-if="selectedContent === 'lesson'">
          <div class="player-surface" v-if="currentLesson.contentType === 'YOUTUBE'">
            <LiteYouTubeEmbed
              :id="getYoutubeId(currentLesson.contentUrl)"
              class="video-embed"
              title="Vídeo da Lição"
            />
          </div>

          <div class="player-surface player-surface--document" v-else-if="currentLesson.contentType === 'PDF'">
            <embed
              :src="'https://docs.google.com/gview?embedded=true&url=' + currentLesson.contentUrl"
              type="application/pdf"
              width="100%"
              height="100%"
            />
          </div>

          <div class="player-surface" v-else-if="currentLesson.contentType === 'PANDA'">
            <iframe
              :src="currentPandaVideo.contentUrl"
              style="border:none;"
              allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
              allowfullscreen
              width="100%"
              height="100%"
              fetchpriority="high"
            ></iframe>
          </div>

          <div class="quiz-shell" v-else-if="currentLesson.contentType === 'QUIZ'">
            <QuizPlayer
              :lesson="currentLesson"
              :course-id="course?.id"
              @quiz-completed="handleQuizCompleted"
            />
          </div>

          <div class="empty-state" v-else>
            <i class="fas fa-file-alt"></i>
            <h3>Conteúdo indisponível</h3>
            <p>Este formato ainda não possui visualização nesse layout.</p>
          </div>
        </section>

        <section class="blocked-card" v-if="selectedContent === 'blocked'">
          <div class="blocked-card__icon">
            <i class="fas fa-lock"></i>
          </div>
          <h3>Conteúdo bloqueado</h3>
          <p>{{ lessonBlockedInfo?.blockedReason || 'Esta aula está bloqueada.' }}</p>
          <p v-if="lessonBlockedInfo?.requiredQuizLessonTitle">
            Complete o quiz <strong>{{ lessonBlockedInfo.requiredQuizLessonTitle }}</strong> para liberar esta etapa.
          </p>
        </section>

        <section class="certificate-card" v-if="selectedContent === 'certificate'">
          <div class="certificate-card__head">
            <div>
              <p class="eyebrow">Conquista</p>
              <h3>Certificado disponível</h3>
            </div>
            <a v-if="certificateUrl" :href="certificateUrl" download class="download-button">
              <i class="fa fa-download"></i>
              <span>Baixar</span>
            </a>
          </div>
          <iframe v-if="certificateUrl" :src="certificateUrl" frameborder="0"></iframe>
        </section>
      </main>
    </div>

    <div class="sidebar-overlay" :class="{ 'sidebar-overlay--active': sidebarOpen }" @click="toggleSidebar"></div>
  </div>
</template>

<script setup>
import ApiService from '@/services/ApiService';
import { tratarError } from '@/axiosConfig';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import LiteYouTubeEmbed from 'vue-lite-youtube-embed';
import { storeToRefs } from 'pinia';
import { useLoadinStore } from '@/stores/loadingStore';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import QuizPlayer from '@/components/quiz/QuizPlayer.vue';
import { JWTHandler } from '@/jwtHandler';

const loadingStore = storeToRefs(useLoadinStore());
const route = useRoute();
const slug = route.params.slug;
const courseId = route.params.id;
const isPreview = !!courseId;

const studentName = ref('');
const course = ref(null);
const isCertificateAvailable = ref(false);
const currentLesson = ref({});
const currentModule = ref({});
const selectedContent = ref('lesson');
const certificateUrl = ref('');
const currentPandaVideo = ref({});
const sidebarOpen = ref(false);
const expandedModules = ref({});
const lessonBlockedInfo = ref(null);

const enabledLessons = computed(() =>
  course.value?.modules?.flatMap((module) =>
    (module.lessons || [])
      .filter((lesson) => lesson.isEnabled)
      .map((lesson) => ({ module, lesson })),
  ) || [],
);

const currentLessonIndex = computed(() =>
  enabledLessons.value.findIndex((entry) => entry.lesson.id === currentLesson.value.id),
);

const currentLessonIndexLabel = computed(() => {
  if (currentLessonIndex.value < 0) {
    return '0/0';
  }
  return `${currentLessonIndex.value + 1}/${enabledLessons.value.length}`;
});

const previousLesson = computed(() => {
  if (currentLessonIndex.value <= 0) {
    return null;
  }
  return enabledLessons.value[currentLessonIndex.value - 1];
});

const nextLesson = computed(() => {
  if (currentLessonIndex.value < 0 || currentLessonIndex.value >= enabledLessons.value.length - 1) {
    return null;
  }
  return enabledLessons.value[currentLessonIndex.value + 1];
});

const contentTitle = computed(() => {
  if (selectedContent.value === 'certificate') {
    return 'Seu certificado';
  }
  if (selectedContent.value === 'blocked') {
    return 'Acesso restrito';
  }
  return currentLesson.value.title || 'Selecione uma aula';
});

const contentSubtitle = computed(() => {
  if (selectedContent.value === 'certificate') {
    return 'Visualize e baixe o comprovante de conclusão do curso.';
  }
  if (selectedContent.value === 'blocked') {
    return 'Há um pré-requisito antes de continuar esta trilha.';
  }
  if (currentLesson.value.contentType === 'QUIZ') {
    return 'Responda às perguntas com calma. Seu progresso fica nesta etapa.';
  }
  return 'Ambiente otimizado para foco, navegação rápida e leitura do conteúdo.';
});

onMounted(async () => {
  try {
    let response;
    if (isPreview) {
      response = await ApiService.get(`/courses/${courseId}/preview`);
      course.value = response.data.course;
      studentName.value = response.data.student || JWTHandler.getUserData()?.fullName || 'Preview';
      isCertificateAvailable.value = false;
    } else {
      response = await ApiService.get(`/enrollments/${slug}/course`);
      course.value = response.data.course;
      studentName.value = response.data.student;
      isCertificateAvailable.value = response.data.isCertificateAvailable;
    }

    if (course.value?.modules?.length > 0 && course.value.modules[0].lessons?.length > 0) {
      expandedModules.value[0] = true;
      const firstLesson = course.value.modules[0].lessons.find((lesson) => lesson.isEnabled);
      if (firstLesson) {
        await selectLesson(course.value.modules[0], firstLesson);
      }
    }
  } catch (error) {
    tratarError(error);
    console.error('Erro ao carregar curso:', error);
  }
});

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value;
};

const toggleModule = (index) => {
  expandedModules.value[index] = !expandedModules.value[index];
};

const checkLessonAccess = async (lesson) => {
  if (isPreview && lesson.contentType !== 'PANDA') {
    return {
      isBlocked: false,
      contentUrl: lesson.contentUrl,
    };
  }

  try {
    const response = await ApiService.get(`/lessons/${lesson.id}/panda`);
    return {
      isBlocked: response.data.isBlocked || false,
      blockedReason: response.data.blockedReason,
      requiredQuizLessonTitle: response.data.requiredQuizLessonTitle,
      contentUrl: response.data.contentUrl,
    };
  } catch (error) {
    tratarError(error);
    console.error('Erro ao verificar acesso à lição:', error);
    return { isBlocked: false, contentUrl: lesson.contentUrl };
  }
};

const selectLesson = async (module, lesson) => {
  currentLesson.value = lesson;
  currentModule.value = module;
  selectedContent.value = 'lesson';
  lessonBlockedInfo.value = null;

  if (window.innerWidth < 1100) {
    sidebarOpen.value = false;
  }

  if (!isPreview && (lesson.contentType === 'YOUTUBE' || lesson.contentType === 'PDF' || lesson.contentType === 'QUIZ')) {
    const accessInfo = await checkLessonAccess(lesson);
    if (accessInfo.isBlocked) {
      lessonBlockedInfo.value = accessInfo;
      selectedContent.value = 'blocked';
      return;
    }
  }

  if (lesson.contentType === 'PANDA') {
    await loadPandaVideo(lesson);
  }
};

const loadPandaVideo = async (lesson) => {
  currentPandaVideo.value = {};
  const accessInfo = await checkLessonAccess(lesson);

  if (accessInfo.isBlocked) {
    lessonBlockedInfo.value = accessInfo;
    selectedContent.value = 'blocked';
    return;
  }

  currentPandaVideo.value = { contentUrl: accessInfo.contentUrl };
  selectedContent.value = 'lesson';
};

const selectCertificate = async () => {
  if (isPreview) {
    return;
  }

  try {
    const response = await ApiService.get(`/enrollments/certificate/${slug}`);
    certificateUrl.value = response.data;
    selectedContent.value = 'certificate';
  } catch (error) {
    tratarError(error);
    console.error('Erro ao carregar certificado:', error);
  }

  if (window.innerWidth < 1100) {
    sidebarOpen.value = false;
  }
};

const goToLesson = async (entry) => {
  if (!entry) {
    return;
  }
  await selectLesson(entry.module, entry.lesson);
};

const handleQuizCompleted = () => {
  console.log('Quiz completed');
};

const getEnabledLessonsCount = (module) => {
  const count = module.lessons?.filter((lesson) => lesson.isEnabled).length || 0;
  return count === 1 ? '1 aula' : `${count} aulas`;
};

const getLessonType = (contentType) => {
  const types = {
    YOUTUBE: 'Vídeo',
    PANDA: 'Panda Video',
    PDF: 'Documento',
    QUIZ: 'Quiz',
  };
  return types[contentType] || 'Conteúdo';
};

const getYoutubeId = (url) => {
  if (!url) {
    return '';
  }

  if (url.includes('youtu.be/')) {
    return url.split('youtu.be/')[1]?.split('?')[0] || '';
  }

  if (url.includes('v=')) {
    return url.split('v=')[1]?.split('&')[0] || '';
  }

  return url;
};
</script>

<style scoped>
.course-layout-v4 {
  --surface: #f4f1ea;
  --surface-strong: #fbf8f2;
  --panel: rgba(255, 255, 255, 0.78);
  --panel-strong: #ffffff;
  --text: #211d17;
  --muted: #72685b;
  --line: rgba(100, 84, 63, 0.14);
  --accent: #1f6b52;
  --accent-strong: #154d3b;
  --accent-soft: rgba(31, 107, 82, 0.1);
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(31, 107, 82, 0.12), transparent 30%),
    radial-gradient(circle at bottom right, rgba(180, 132, 74, 0.12), transparent 26%),
    var(--surface);
  color: var(--text);
  font-family: "DM Sans", "Segoe UI", sans-serif;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 28px;
  border-bottom: 1px solid var(--line);
  background: rgba(244, 241, 234, 0.86);
  backdrop-filter: blur(18px);
}

.topbar__left,
.topbar__right,
.brand,
.brand__copy,
.student-chip,
.module-card__meta,
.hero-card__actions,
.nav-button,
.certificate-card__head,
.download-button {
  display: flex;
  align-items: center;
}

.topbar__left,
.brand,
.student-chip,
.module-card__meta,
.hero-card__actions,
.nav-button,
.download-button {
  gap: 12px;
}

.brand {
  color: inherit;
  text-decoration: none;
}

.brand__logo {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.brand__copy {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.brand__label,
.eyebrow,
.module-card__label,
.student-chip__label {
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted);
}

.student-chip {
  padding: 10px 14px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.55);
}

.student-chip strong {
  font-size: 14px;
}

.shell {
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 24px;
  padding: 24px;
}

.sidebar__panel,
.hero-card,
.player-card,
.blocked-card,
.certificate-card {
  border: 1px solid var(--line);
  border-radius: 28px;
  background: var(--panel);
  backdrop-filter: blur(18px);
  box-shadow: 0 20px 60px rgba(55, 43, 28, 0.08);
}

.sidebar {
  position: relative;
}

.sidebar__panel {
  position: sticky;
  top: 92px;
  padding: 22px;
}

.sidebar__head,
.sidebar__summary {
  display: flex;
  justify-content: space-between;
  gap: 14px;
}

.sidebar__summary {
  margin: 20px 0 24px;
}

.summary-card {
  flex: 1;
  padding: 16px;
  border-radius: 20px;
  background: var(--surface-strong);
  border: 1px solid var(--line);
}

.summary-card span,
.lesson-item__body small,
.hero-card__subtitle,
.blocked-card p,
.empty-state p,
.certificate-entry__body small {
  color: var(--muted);
}

.summary-card strong {
  display: block;
  margin-top: 8px;
  font-size: 24px;
}

.module-list {
  display: grid;
  gap: 14px;
  max-height: calc(100vh - 210px);
  overflow: auto;
  padding-right: 4px;
}

.module-card {
  border: 1px solid var(--line);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.62);
}

.module-card__header,
.lesson-item,
.certificate-entry,
.icon-button,
.nav-button,
.download-button {
  border: 0;
  cursor: pointer;
}

.module-card__header,
.lesson-item,
.certificate-entry {
  width: 100%;
  text-align: left;
}

.module-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px;
  background: transparent;
}

.module-card__header h3,
.hero-card h1,
.blocked-card h3,
.empty-state h3,
.certificate-card h3 {
  margin: 0;
}

.module-card__header h3 {
  font-size: 22px;
  line-height: 1.15;
  font-weight: 700;
}

.lesson-list {
  display: grid;
  gap: 8px;
  padding: 0 12px 14px;
}

.lesson-item {
  display: flex;
  gap: 12px;
  padding: 14px;
  border-radius: 18px;
  background: transparent;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.lesson-item:hover,
.lesson-item--active {
  background: var(--surface-strong);
}

.lesson-item--active {
  outline: 1px solid rgba(31, 107, 82, 0.22);
}

.lesson-item__icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 12px;
  background: var(--accent-soft);
  color: var(--accent);
}

.lesson-item__body {
  display: grid;
  gap: 4px;
}

.lesson-item__body strong {
  font-size: 15px;
  line-height: 1.35;
  font-weight: 700;
}

.certificate-entry {
  display: flex;
  gap: 14px;
  padding: 16px;
  border-radius: 22px;
  background: linear-gradient(135deg, rgba(31, 107, 82, 0.1), rgba(180, 132, 74, 0.12));
}

.certificate-entry__icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.7);
  color: var(--accent-strong);
}

.content {
  display: grid;
  gap: 20px;
}

.hero-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 26px 28px;
}

.hero-card h1 {
  font-size: clamp(24px, 2.2vw, 34px);
  line-height: 1.08;
  max-width: 980px;
  word-break: break-word;
}

.hero-card__subtitle {
  max-width: 720px;
  margin: 10px 0 0;
  font-size: 15px;
}

.nav-button {
  padding: 12px 16px;
  border-radius: 999px;
  background: #fff;
  color: var(--text);
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.nav-button--primary,
.download-button {
  background: var(--accent);
  color: #fff;
}

.nav-button:disabled {
  opacity: 0.45;
  cursor: default;
}

.player-card,
.certificate-card {
  padding: 20px;
}

.player-surface {
  overflow: hidden;
  aspect-ratio: 16 / 9;
  min-height: 0;
  border-radius: 22px;
  background: #111;
}

.player-surface--document {
  background: #ebe6db;
}

.player-surface iframe,
.player-surface embed,
.video-embed {
  width: 100%;
  height: 100%;
  display: block;
}

.quiz-shell {
  padding: 8px;
}

.blocked-card,
.empty-state {
  display: grid;
  place-items: center;
  min-height: 420px;
  padding: 30px;
  text-align: center;
}

.blocked-card__icon,
.empty-state i {
  display: grid;
  place-items: center;
  width: 72px;
  height: 72px;
  margin: 0 auto 16px;
  border-radius: 24px;
  background: rgba(31, 107, 82, 0.1);
  color: var(--accent);
  font-size: 26px;
}

.certificate-card {
  display: grid;
  gap: 18px;
}

.certificate-card__head {
  justify-content: space-between;
  gap: 14px;
}

.certificate-card iframe {
  width: 100%;
  min-height: 720px;
  border: 0;
  border-radius: 18px;
  background: #fff;
}

.download-button {
  padding: 12px 18px;
  border-radius: 999px;
  text-decoration: none;
}

.icon-button {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.7);
}

.mobile-only {
  display: none;
}

.sidebar-overlay {
  position: fixed;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  background: rgba(23, 18, 13, 0.4);
  transition: opacity 0.2s ease;
}

.sidebar-overlay--active {
  opacity: 1;
  pointer-events: auto;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.18s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.is-open {
  transform: rotate(180deg);
}

@media (max-width: 1099px) {
  .shell {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 30;
    width: min(92vw, 380px);
    height: 100vh;
    transform: translateX(-104%);
    transition: transform 0.22s ease;
  }

  .sidebar--open {
    transform: translateX(0);
  }

  .sidebar__panel {
    top: 0;
    height: 100%;
    border-radius: 0 28px 28px 0;
    overflow: hidden;
  }

  .mobile-only {
    display: grid;
  }

  .topbar {
    padding: 16px 18px;
  }

  .student-chip {
    display: none;
  }

  .hero-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .player-surface {
    aspect-ratio: 16 / 9;
  }
}

@media (max-width: 767px) {
  .shell {
    padding: 14px;
  }

  .topbar__right {
    display: none;
  }

  .brand__copy strong {
    max-width: 190px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .hero-card,
  .player-card,
  .blocked-card,
  .certificate-card {
    border-radius: 22px;
  }

  .hero-card {
    padding: 20px;
  }

  .hero-card__actions {
    width: 100%;
  }

  .nav-button {
    flex: 1;
    justify-content: center;
  }

  .player-surface {
    aspect-ratio: 16 / 9;
  }

  .certificate-card iframe {
    min-height: 520px;
  }
}
</style>
